import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getApiUrl, getSettings } from "../utils/apiClient";

const DEFAULT_SITE_IDENTITY = "Søren Kølbæk";

const SiteSettingsContext = createContext({
  siteIdentity: DEFAULT_SITE_IDENTITY,
  faviconUrl: null,
  loading: true,
  refreshSettings: async () => {},
});

function applyFavicon(faviconUrl) {
  const links = document.querySelectorAll("link[rel*='icon']");
  links.forEach((node) => node.parentNode?.removeChild(node));

  if (!faviconUrl) return;

  const href = faviconUrl.startsWith("http") ? faviconUrl : `${getApiUrl()}${faviconUrl}`;
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = href;
  document.head.appendChild(link);
}

export function SiteSettingsProvider({ children }) {
  const [siteIdentity, setSiteIdentity] = useState(DEFAULT_SITE_IDENTITY);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await getSettings();
      const identity = (data?.site_identity || "").trim() || DEFAULT_SITE_IDENTITY;
      setSiteIdentity(identity);
      setFaviconUrl(data?.favicon_url || null);
      applyFavicon(data?.favicon_url || null);
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const value = useMemo(
    () => ({
      siteIdentity,
      faviconUrl,
      loading,
      refreshSettings,
    }),
    [siteIdentity, faviconUrl, loading, refreshSettings]
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

export function formatDocumentTitle(siteIdentity, pageTitle) {
  const identity = (siteIdentity || "").trim() || DEFAULT_SITE_IDENTITY;
  const title = (pageTitle || "").trim();
  return title ? `${identity} - ${title}` : identity;
}

export default SiteSettingsContext;
